import { aegis } from "@/utils/monitoring";
import { useUserStore } from '@/store/user';
import { usePayStore } from '@/store/pay';

type RequestFunction<T> = (...args: any[]) => Promise<T>;
type CompletionCheck<T> = (result: T) => PollResult;
type ErrorHandler = (error: Error) => boolean;
type EventCallback<T> = (result: T) => void;

interface PollerOptions<T> {
    /** 轮询的时间间隔（毫秒） */
    interval?: number;
    /** 是否打印轮询的总耗时和轮询次数 */
    shouldPrint?: boolean;
    /** 最大轮询次数 */
    maxRetries?: number;
    /** 总超时时间（毫秒） */
    timeout?: number;
    /** 自定义错误处理函数 */
    errorHandler?: ErrorHandler;
    /** 是否需要上报日志 */
    shouldLog?: boolean;
    /** 日志消息 */
    logMessage?: string;
    /** 是否无限循环轮询 */
    loop?: boolean;
}

// 默认配置
const DEFAULT_POLLER_OPTIONS: Required<PollerOptions<any>> = {
    interval: 1000,      // 默认间隔时间1000毫秒
    shouldPrint: false,  // 默认打印总耗时和轮询次数
    maxRetries: 100,// 最大轮询次数
    timeout: 60000 * 5,  // 超时5分钟
    errorHandler: (error: any) => {
        console.warn('Polling error:', error);

        // 算力不足
        if (error?.code === -100) {
            const userStore = useUserStore();
            const payStore = usePayStore();

            payStore.ballParams.button = 'button_creation_img_not_points';
            userStore.usePayModal({ freeToast: false, powerPackage: true });
        }

        return error instanceof PollingError; // 如果是自定义的PollingError，返回true
    }, // 默认无错误处理函数
    shouldLog: false,
    logMessage: '',
    loop: false
};

// 定义状态常量  
const STATUS = {
    FAILED: -1,
    PROCESSING: 2,
    COMPLETED: 3,
};

// 定义轮询结果的枚举
enum PollResult {
    CONTINUE, // 继续轮询
    COMPLETE, // 完成，调用成功回调
    CANCEL    // 取消轮询
}

class PollingError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'PollingError';
    }
}

// 添加通用响应接口
interface BasePollingResponse {
    data: {
        status: number;
        [key: string]: any;
    };
}

// 添加默认的检查完成函数
const defaultCheckCompletion = <T extends BasePollingResponse>(result: T): PollResult => {
    switch (result.data.status) {
        case STATUS.COMPLETED:
            return PollResult.COMPLETE;
        case STATUS.FAILED:
            return PollResult.CANCEL;
        case STATUS.PROCESSING:
            return PollResult.CONTINUE;
        default:
            throw new PollingError('未知状态');
    }
};

class Poller<T> {
    // 配置参数  
    private interval: number;
    private shouldPrint: boolean;
    private maxRetries: number;
    private timeout?: number;
    private errorHandler?: ErrorHandler;
    private shouldLog: boolean;
    private logMessage: string;
    private loop: boolean;

    // 状态追踪  
    private retries: number = 0;
    private performance: {
        startTime?: number;
        endTime?: number;
        totalPolls: number;
    } = { totalPolls: 0 };

    // 事件监听器  
    private listeners: {
        onPoll?: EventCallback<T>;
        onComplete?: EventCallback<T>;
        onError?: (error: Error) => void;
    } = {};

    // 取消标志  
    private isCanceled: boolean = false;

    /**  
     * 创建一个 Poller 实例  
     * @param options - 轮询选项  
     */
    constructor(options: PollerOptions<T> = {}) {
        const mergedOptions = { ...DEFAULT_POLLER_OPTIONS, ...options };
        this.interval = mergedOptions.interval;
        this.shouldPrint = mergedOptions.shouldPrint;
        this.maxRetries = mergedOptions.maxRetries;
        this.timeout = mergedOptions.timeout;
        this.errorHandler = mergedOptions.errorHandler;
        this.shouldLog = mergedOptions.shouldLog;
        this.logMessage = mergedOptions.logMessage;
        this.loop = mergedOptions.loop;
    }

    /**  
     * 添加事件监听器  
     * @param event - 事件类型  
     * @param callback - 回调函数  
     * @returns this - 支持链式调用  
     */
    on(event: 'poll' | 'complete' | 'error', callback: EventCallback<T> | ((error: Error) => void)): this {
        switch (event) {
            case 'poll':
                this.listeners.onPoll = callback as EventCallback<T>;
                break;
            case 'complete':
                this.listeners.onComplete = callback as EventCallback<T>;
                break;
            case 'error':
                this.listeners.onError = callback as (error: Error) => void;
                break;
        }
        return this;
    }

    /**  
     * 启动轮询  
     * @param requestFn - 执行请求的函数  
     * @param checkCompletion - 处理请求结果的回调  
     * @param args - 传递给请求函数的参数  
     * @returns 最后一次通过判断条件的结果  
     */
    async poll(requestFn: RequestFunction<T>, checkCompletion: CompletionCheck<T>, ...args: any[]): Promise<T | undefined> {
        this.isCanceled = false;
        this.performance.startTime = performance.now();
        this.performance.totalPolls = 0;

        if (this.shouldPrint) {
            console.time("Poll Duration");
        }

        let finalResult: T | undefined;
        const startTime = Date.now();

        try {
            while (
                (this.retries < this.maxRetries || this.loop) &&
                !this.isCanceled &&
                (!this.timeout || Date.now() - startTime < this.timeout || this.loop)
            ) {
                try {
                    const result = await requestFn(...args);
                    this.retries++;
                    this.performance.totalPolls++;

                    if (this.listeners.onPoll) {
                        this.listeners.onPoll(result);
                    }

                    // 使用简化的检查逻辑
                    const pollResult = checkCompletion(result);
                    finalResult = result;

                    switch (pollResult) {
                        case PollResult.COMPLETE:
                            if (this.shouldLog && this.performance.startTime) {
                                aegis?.infoAll({
                                    msg: this.logMessage,
                                    ext1: `${((performance.now() - this.performance.startTime) / 1000).toFixed(2)}s`,
                                });
                            }
                            if (this.listeners.onComplete) {
                                this.listeners.onComplete(result);
                            }
                            return result;
                        case PollResult.CANCEL:
                            this.cancel();
                            return result;
                        case PollResult.CONTINUE:
                            // 打印已运行时间
                            if (this.shouldPrint) {
                                const runningTime = ((Date.now() - startTime) / 1000).toFixed(2);
                                console.log(`已运行 ${runningTime} 秒`);
                            }
                            await this.delay(this.interval);
                            continue;
                    }
                } catch (error) {
                    // 自定义错误处理  
                    if (this.errorHandler && this.errorHandler(error as Error)) {
                        await this.delay(this.interval);
                        continue;
                    }

                    // 增加错误日志
                    aegis?.error({
                        msg: this.logMessage,
                        ext1: `${error}`,
                    });

                    // 触发错误事件  
                    if (this.listeners.onError) {

                        this.listeners.onError(error as Error);
                    }

                    throw error;
                }
            }

            // 检查是否因超时或重试次数达到上限而结束  
            if (this.retries >= this.maxRetries) {
                throw new PollingError('Max retries reached');
            }

            if (this.timeout && Date.now() - startTime >= this.timeout) {
                throw new PollingError('Polling timeout');
            }

            return finalResult; // 返回最后一次结果  
        } finally {
            // 性能追踪结束  
            this.performance.endTime = performance.now();

            // 打印性能信息  
            if (this.shouldPrint) {
                console.timeEnd("Poll Duration");
                console.log(`Polling Summary:  
    Total Time: ${this.performance.endTime - this.performance.startTime}ms  
    Total Polls: ${this.performance.totalPolls}`);
            }
        }
    }

    /**  
     * 延迟指定的毫秒数  
     * @param ms - 延迟的时间（毫秒）  
     * @returns 返回一个等待的 Promise  
     */
    private delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    /**  
     * 取消轮询  
     */
    cancel(): void {
        this.isCanceled = true;
    }

    /**  
     * 获取轮询性能信息  
     * @returns 性能追踪数据  
     */
    getPerformance() {
        return {
            totalTime: this.performance.endTime ?
                this.performance.endTime - (this.performance.startTime || 0) : 0,
            totalPolls: this.performance.totalPolls
        };
    }
}

// 导出  
export {
    Poller,
    PollingError,
    PollResult,
    type RequestFunction,
    type CompletionCheck,
    type PollerOptions,
    STATUS,
    defaultCheckCompletion, // 导出默认检查函数
    type BasePollingResponse // 导出基础响应接口
};
